import { useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { api } from '@/api/client'
import { useAuth } from '@/api/hooks/useAuth'
import { Button } from '@/components/ui/button'
import {
  Instagram, Plus, Loader2, RefreshCw, CheckCircle2, XCircle, MinusCircle, Send, MessageCircle, History,
} from 'lucide-react'

// ── Tipos (GET /ig-manager/accounts) ──

interface AppStatus {
  app: string
  ok: boolean
  error: string | null
  webhooks: string[]
}

interface IgManagerAccount {
  id: string
  name: string
  username: string | null
  igUserId: string
  picture: string | null
  followers: number | null
  isActive: boolean
  posting: AppStatus & { expires: string | null }
  messaging: AppStatus & { connected: boolean; since: string | null }
  checkedAt: string
}

const RETURN_TO = '/instagram/gerenciador'

interface RecoverSummary {
  posts: number
  comments: number
  alreadySent: number
  repeatedPerson: number
  expiring: number
  toSend: number
  people: number
  minutes: number
  errors: string[]
}

function fmtDate(iso: string | null) {
  if (!iso) return '-'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function daysLeft(iso: string | null) {
  if (!iso) return null
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)
}

/**
 * Gerenciador do Instagram: conecta cada conta nos dois apps, igual ao Gerenciador
 * de Páginas do Facebook. Minibot = postagem; Broadzinho = card, comentários e DMs.
 */
export function IgManager() {
  const { data: user } = useAuth()
  const qc = useQueryClient()
  const location = useLocation()

  const { data, isLoading, isFetching } = useQuery<{ accounts: IgManagerAccount[] }>({
    queryKey: ['ig-manager', 'accounts'],
    queryFn: () => api.get<{ accounts: IgManagerAccount[] }>('/ig-manager/accounts'),
  })

  const refresh = async () => {
    const fresh = await api.get<{ accounts: IgManagerAccount[] }>('/ig-manager/accounts?refresh=1')
    qc.setQueryData(['ig-manager', 'accounts'], fresh)
  }

  const uid = encodeURIComponent(user?.id || '')
  const back = encodeURIComponent(RETURN_TO)
  const connectPosting = () => { window.location.href = `/oauth/instagram/redirect?userId=${uid}&returnTo=${back}` }
  const connectMessaging = () => { window.location.href = `/oauth/instagram/redirect?app=msg&userId=${uid}&returnTo=${back}` }

  // Retorno do OAuth (?success=... ou ?error=...&message=...)
  const notice = useMemo(() => {
    const q = new URLSearchParams(location.search)
    const ok = q.get('success')
    const err = q.get('error')
    if (ok === 'ig_connected') return { ok: true, text: `Postagem conectada pelo Minibot: ${q.get('accounts') || ''}` }
    if (ok === 'ig_msg_connected') return { ok: true, text: `Mensagens conectadas pelo Broadzinho: ${q.get('accounts') || ''}` }
    if (err) return { ok: false, text: q.get('message') || err }
    return null
  }, [location.search])

  const accounts = data?.accounts ?? []

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Instagram className="w-6 h-6 text-[#e1306c]" /> Gerenciador do Instagram
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Cada conta usa dois apps: o <strong className="text-zinc-200">Minibot</strong> para postagem e o{' '}
            <strong className="text-zinc-200">Broadzinho</strong> para card, comentários e mensagens.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <Button onClick={connectPosting} size="sm" className="gap-2" title="Login no app Minibot (postagem)">
            <Plus className="w-4 h-4" /> Conectar postagem
          </Button>
          <Button onClick={connectMessaging} size="sm" variant="outline" className="gap-2" title="Login no app Broadzinho (card, comentários e mensagens)">
            <Plus className="w-4 h-4" /> Conectar mensagens
          </Button>
          <Button onClick={refresh} size="sm" variant="ghost" className="gap-2" disabled={isFetching}>
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /> Verificar
          </Button>
        </div>
      </div>

      {notice && (
        <div className={`px-3 py-2 rounded-lg text-sm border ${notice.ok
          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
          : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {notice.text}
        </div>
      )}

      <div className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-400 space-y-1">
        <p>1. Entre no Instagram, no navegador, com a conta que vai conectar.</p>
        <p>2. <strong className="text-zinc-200">Conectar postagem</strong> primeiro (cria a conta no painel), depois <strong className="text-zinc-200">Conectar mensagens</strong>.</p>
        <p>3. Enquanto o app estiver em desenvolvimento, a conta precisa ser testadora aceita em cada app.</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
      ) : accounts.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center space-y-3">
          <Instagram className="w-12 h-12 text-zinc-600 mx-auto" />
          <h2 className="text-white font-semibold text-lg">Nenhuma conta do Instagram conectada</h2>
          <Button onClick={connectPosting} className="gap-2"><Plus className="w-4 h-4" /> Conectar postagem</Button>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {accounts.map(a => <AccountCard key={a.id} a={a} />)}
        </div>
      )}
    </div>
  )
}

function AccountCard({ a }: { a: IgManagerAccount }) {
  const left = daysLeft(a.posting.expires)
  return (
    <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl space-y-3">
      <div className="flex items-center gap-3">
        {a.picture
          ? <img src={a.picture} className="w-12 h-12 rounded-full object-cover shrink-0" />
          : <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center shrink-0 text-lg font-bold text-zinc-400">{(a.username || a.name || '?')[0]}</div>}
        <div className="min-w-0 flex-1">
          <p className="text-white font-medium text-sm truncate">@{a.username || a.name}</p>
          <p className="text-zinc-500 text-xs truncate">
            {a.name}{a.followers != null ? ` · ${a.followers.toLocaleString('pt-BR')} seguidores` : ''}{!a.isActive ? ' · pausada' : ''}
          </p>
        </div>
      </div>

      <AppRow
        icon={<Send className="w-3.5 h-3.5" />}
        title="Postagem"
        app={a.posting.app}
        state={a.posting.ok ? 'ok' : 'erro'}
        detail={a.posting.ok
          ? `token vence em ${fmtDate(a.posting.expires)}${left != null ? ` (${left} dias)` : ''}`
          : (a.posting.error || 'token inválido: conecte a postagem de novo')}
        webhooks={a.posting.webhooks}
      />
      <AppRow
        icon={<MessageCircle className="w-3.5 h-3.5" />}
        title="Mensagens"
        app={a.messaging.app}
        state={!a.messaging.connected ? 'falta' : a.messaging.ok ? 'ok' : 'erro'}
        detail={!a.messaging.connected
          ? 'não conectada: o card ainda sai pelo Minibot'
          : a.messaging.ok
            ? `conectada em ${fmtDate(a.messaging.since)}`
            : (a.messaging.error || 'token inválido: conecte as mensagens de novo')}
        webhooks={a.messaging.webhooks}
      />
      <RecoverBox accountId={a.id} />
    </div>
  )
}

/**
 * Recuperar comentários dos últimos 7 dias: primeiro só conta (nada é enviado),
 * mostra os números e só enfileira depois da confirmação.
 */
function RecoverBox({ accountId }: { accountId: string }) {
  const [step, setStep] = useState<'idle' | 'scanning' | 'review' | 'sending' | 'done' | 'error'>('idle')
  const [sum, setSum] = useState<RecoverSummary | null>(null)
  const [msg, setMsg] = useState('')

  const scan = async () => {
    setStep('scanning'); setMsg('')
    try {
      const r = await api.post<{ summary: RecoverSummary }>(`/ig-manager/${accountId}/recover?dry=1`, {})
      setSum(r.summary); setStep('review')
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Falha na varredura'); setStep('error')
    }
  }
  const send = async () => {
    setStep('sending')
    try {
      const r = await api.post<{ queued: number; summary: RecoverSummary }>(`/ig-manager/${accountId}/recover`, {})
      setMsg(`${r.queued} cards na fila. Saem 1 a cada 6 segundos, cerca de ${r.summary.minutes} minutos.`)
      setStep('done')
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Falha ao enviar'); setStep('error')
    }
  }

  return (
    <div className="px-3 py-2 rounded-lg bg-zinc-800/50 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-zinc-200 flex items-center gap-1.5">
          <History className="w-3.5 h-3.5" /> <strong>Recuperar comentários</strong>
          <span className="text-zinc-500">· últimos 7 dias</span>
        </p>
        {(step === 'idle' || step === 'done' || step === 'error') && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={scan}>Verificar</Button>
        )}
        {step === 'scanning' && <span className="text-[11px] text-zinc-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> lendo os posts...</span>}
      </div>

      {step === 'review' && sum && (
        <div className="space-y-2">
          <p className="text-[11px] text-zinc-400">
            {sum.comments} comentários em {sum.posts} posts. {sum.alreadySent} já receberam card,{' '}
            {sum.repeatedPerson} são repetidos da mesma pessoa
            {sum.expiring > 0 ? `, ${sum.expiring} venceriam antes de sair` : ''}.
          </p>
          <p className="text-xs text-white">
            <strong>{sum.toSend}</strong> pessoas vão receber o card, 1 a cada 6 segundos (cerca de {sum.minutes} min).
          </p>
          {sum.errors.length > 0 && <p className="text-[10px] text-amber-400 break-words">Avisos: {sum.errors.join(' · ')}</p>}
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" disabled={sum.toSend === 0} onClick={send}>Enviar {sum.toSend} cards</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setStep('idle')}>Cancelar</Button>
          </div>
        </div>
      )}
      {step === 'sending' && <p className="text-[11px] text-zinc-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> colocando na fila...</p>}
      {(step === 'done' || step === 'error') && msg && (
        <p className={`text-[11px] ${step === 'done' ? 'text-emerald-400' : 'text-red-400'}`}>{msg}</p>
      )}
    </div>
  )
}

function AppRow({ icon, title, app, state, detail, webhooks }: {
  icon: ReactNode
  title: string
  app: string
  state: 'ok' | 'erro' | 'falta'
  detail: string
  webhooks: string[]
}) {
  const Icon = state === 'ok' ? CheckCircle2 : state === 'erro' ? XCircle : MinusCircle
  const color = state === 'ok' ? 'text-emerald-400' : state === 'erro' ? 'text-red-400' : 'text-zinc-500'
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-zinc-800/50">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-zinc-200 flex items-center gap-1.5">
          {icon} <strong>{title}</strong> <span className="text-zinc-500">· {app}</span>
        </p>
        <p className="text-[11px] text-zinc-400 mt-0.5 break-words">{detail}</p>
        {webhooks.length > 0 && (
          <p className="text-[10px] text-zinc-500 mt-0.5">recebe eventos: {webhooks.join(', ')}</p>
        )}
      </div>
    </div>
  )
}
